/**
 * ============================================================
 * KEJJA OS — src/hooks/useInstallPrompt.ts
 * Captures the PWA beforeinstallprompt event so we can trigger
 * install programmatically. On iOS, we fall back to instructions
 * since iOS doesn't support beforeinstallprompt.
 * ============================================================
 */

import { useCallback, useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export type InstallState = 'unsupported' | 'available' | 'installed' | 'ios';

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [state, setState] = useState<InstallState>('unsupported');

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Detect iOS
    const ua = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua) && !/crios|fxios/.test(ua);

    // Detect if already installed (standalone mode)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as { standalone?: boolean }).standalone === true;

    if (isStandalone) { setState('installed'); return; }

    if (isIOS) { setState('ios'); return; }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setState('available');
    };
    window.addEventListener('beforeinstallprompt', handler);

    const installedHandler = () => {
      setState('installed');
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const triggerInstall = useCallback(async () => {
    if (!deferredPrompt) return { installed: false, reason: 'unavailable' as const };
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (choice.outcome === 'accepted') { setState('installed'); return { installed: true }; }
    return { installed: false, reason: 'dismissed' as const };
  }, [deferredPrompt]);

  return { state, triggerInstall };
}
